import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, DataTable, EmptyState } from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import {
  useMyLocumPay,
  type LocumPayStatus,
  type LocumPayWeek,
} from '@/features/locum-pay/api';
import { weekLabel } from '@/features/locum-pay/format';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import styles from './LocumPayPages.module.css';

const PAY_STEPS = [
  'running',
  'submitted',
  'approved',
  'paid',
] as const satisfies readonly LocumPayStatus[];

const hours = (minutes: number) => `${(minutes / 60).toFixed(2)} h`;

function WeeklyPayTracker({ status }: { status: LocumPayStatus }) {
  const { t } = useTranslation();
  const current = PAY_STEPS.indexOf(status);

  return (
    <ol
      className={styles.stepTracker}
      aria-label={t('locumPay.weeklyProgress')}
    >
      {PAY_STEPS.map((step, index) => (
        <li
          key={step}
          className={
            index < current
              ? styles.stepComplete
              : index === current
                ? styles.stepCurrent
                : styles.stepPending
          }
          aria-current={index === current ? 'step' : undefined}
        >
          <span className={styles.stepMarker} aria-hidden="true">
            {index + 1}
          </span>
          <span>{t(`locumPay.status.${step}`)}</span>
        </li>
      ))}
    </ol>
  );
}

export function MyLocumPayPage() {
  const { t } = useTranslation();
  const pay = useMyLocumPay();
  const notFoundError =
    isApiError(pay.error) && pay.error.code === 'LOCUM_PAY_NOT_FOUND'
      ? pay.error
      : undefined;
  const notFound = notFoundError !== undefined;

  return (
    <PageContainer>
      <Card title={t('locumPay.myTitle')} flush>
        <DataTable
          caption={t('locumPay.myTitle')}
          rows={notFound ? [] : pay.data}
          rowKey={(row) => row.id}
          loading={pay.isLoading}
          error={notFound ? undefined : pay.error}
          onRetry={() => void pay.refetch()}
          empty={
            <div role={notFound ? 'alert' : undefined}>
              <EmptyState
                title={t('locumPay.noOwnPay')}
                body={
                  notFoundError ? (
                    <>
                      {t('locumPay.errors.notFound')}
                      {notFoundError.requestId && (
                        <span className={styles.errorReference}>
                          {t('common.requestId', {
                            id: notFoundError.requestId,
                          })}
                        </span>
                      )}
                    </>
                  ) : (
                    t('locumPay.noOwnPayBody')
                  )
                }
              />
            </div>
          }
          columns={[
            {
              key: 'week',
              header: t('locumPay.week'),
              render: (row: LocumPayWeek) => (
                <div>
                  <strong>{weekLabel(row)}</strong>
                  <span className={styles.muted}>{row.branchName}</span>
                </div>
              ),
            },
            {
              key: 'attendance',
              header: t('locumPay.attendance'),
              render: (row: LocumPayWeek) => hours(row.attendanceMinutes),
            },
            {
              key: 'gross',
              header: t('locumPay.grossPay'),
              render: (row: LocumPayWeek) => (
                <strong>{formatRM(row.grossPaySen)}</strong>
              ),
            },
            {
              key: 'progress',
              header: t('locumPay.progress'),
              render: (row: LocumPayWeek) => (
                <div className={styles.progressCell}>
                  <WeeklyPayTracker status={row.status} />
                  {row.correctionRequired && (
                    <span className={styles.correction}>
                      <AlertTriangle size={14} />
                      {t('locumPay.awaitingAttendanceCorrection')}
                    </span>
                  )}
                </div>
              ),
            },
            {
              key: 'reference',
              header: t('locumPay.bankReference'),
              render: (row: LocumPayWeek) => row.bankReference ?? '\u2014',
            },
          ]}
        />
      </Card>
    </PageContainer>
  );
}
